import { useState } from "react";
import PropTypes from "prop-types";
import { Card, Center, rem } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import {
  HiArrowDownTray,
  HiArrowUpTray,
  HiXMark,
  HiDocument,
  HiXCircle,
  HiExclamationCircle,
  HiExclamationTriangle,
  HiCheckCircle,
  HiArrowRight,
} from "react-icons/hi2";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import {
  MAX_FILE_SIZE,
  REQUIRED_COLUMNS,
  redBannerStyle,
  amberBannerStyle,
  formatFileSize,
} from "../fileUploadConfig";
import { useTemplateDownload } from "../hooks/useTemplateDownload";

function Step1Instructions({ onUpload, onCancel }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  const { downloadTemplate, isDownloading } = useTemplateDownload();

  const handleDrop = (files) => {
    setSelectedFile(files[0]);
    setFileError(null);
  };

  const handleReject = (rejected) => {
    const code = rejected[0]?.errors?.[0]?.code;
    if (code === "file-too-large") {
      setFileError("File size must not exceed 10MB.");
    } else if (code === "file-invalid-type") {
      setFileError("Only .xlsx files are accepted.");
    } else {
      setFileError("File not accepted. Please try again.");
    }
    setSelectedFile(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setFileError(null);
  };

  return (
    <Card shadow="xs" radius="lg" p={{ base: "md", sm: "xl" }}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-base font-bold text-gray-900">New Bulk Payment</p>
          <p className="text-sm text-gray-400">
            Upload your bulk payment file in Excel (.xlsx) format. Ensure all
            mandatory fields are completed according to the template guidelines.
          </p>
        </div>

        <CustomButton
          variant="outline"
          radius="md"
          fullWidth
          leftSection={<HiArrowDownTray size={16} />}
          loading={isDownloading}
          onClick={downloadTemplate}
          loadingText="Downloading Template..."
        >
          Download Template
        </CustomButton>

        {selectedFile ? (
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
              <HiDocument size={20} className="text-primary-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              onClick={handleClear}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors shrink-0"
            >
              <HiXMark size={16} />
            </button>
          </div>
        ) : (
          <Dropzone
            onDrop={handleDrop}
            onReject={handleReject}
            accept={[MIME_TYPES.xlsx]}
            maxSize={MAX_FILE_SIZE}
            multiple={false}
            radius="lg"
            p="xl"
          >
            <Center style={{ flexDirection: "column", gap: rem(12) }}>
              <Dropzone.Idle>
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <HiArrowUpTray size={22} className="text-gray-500" />
                </div>
              </Dropzone.Idle>
              <Dropzone.Accept>
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <HiArrowUpTray size={22} className="text-primary-600" />
                </div>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <HiXCircle size={22} className="text-red-500" />
                </div>
              </Dropzone.Reject>

              <div className="text-center">
                {fileError ? (
                  <p className="text-sm font-semibold text-red-600">
                    {fileError}
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-gray-700">
                    Drag &amp; drop your file here or click to select
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Supported format: XLSX (Max size 10MB)
                </p>
              </div>
            </Center>
          </Dropzone>
        )}

        <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
            <HiExclamationCircle
              size={16}
              className="text-primary-500 shrink-0"
            />
            <p className="text-sm font-semibold text-gray-800">
              Template Instructions
            </p>
          </div>

          <div className="flex flex-col sm:flex-row">
            <div className="flex-1 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-400 mb-3">
                Required Columns
              </p>
              <ul className="flex flex-col gap-2.5">
                {REQUIRED_COLUMNS.map((item) => (
                  <li key={item.label} className="flex items-start gap-2">
                    <HiCheckCircle
                      size={15}
                      className="text-green-500 shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">
                        {item.label}:
                      </span>{" "}
                      {item.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="hidden sm:block w-px bg-gray-200" />
            <div className="sm:hidden h-px bg-gray-200 mx-4" />

            <div className="flex-1 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                File Constraints
              </p>
              <div className="flex flex-col gap-2">
                <CustomInlineBanner
                  styles={redBannerStyle}
                  icon={<HiExclamationTriangle size={14} />}
                >
                  <p className="text-xs font-medium text-red-600">
                    Maximum 100 rows per file
                  </p>
                </CustomInlineBanner>
                <CustomInlineBanner
                  styles={amberBannerStyle}
                  icon={<HiExclamationCircle size={14} />}
                >
                  <p className="text-xs font-medium text-amber-600">
                    Accepted format: .xlsx only
                  </p>
                </CustomInlineBanner>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="font-semibold">Tip:</span> Upload one payment
                  channel per file. For Mpesa include Phone Number; for Bank
                  include Bank Code and Bank Account.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <CustomButton variant="filled" color="red" onClick={onCancel}>
            Cancel
          </CustomButton>
          <CustomButton
            disabled={!selectedFile}
            rightSection={<HiArrowRight size={15} />}
            onClick={() => onUpload(selectedFile)}
          >
            Continue to Upload
          </CustomButton>
        </div>
      </div>
    </Card>
  );
}

Step1Instructions.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default Step1Instructions;
